import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import LOGO_BASE64 from "../../assets/data/logoBase64";
import RangeFilter from "../../components/RangeFilter";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  IconButton,
  Divider,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Chip,
} from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import { ArrowBack } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { MdPhoneAndroid, MdLocationOn } from "react-icons/md";
import Stack from "@mui/material/Stack";
import { FaTrash, FaEdit, FaPrint, FaWhatsapp } from "react-icons/fa";
import TrayBalanceModal from "../../components/TrayBalanceModal";
import TransactionReceipt from "../../components/TransactionReceipt";
import { useAuth } from "../../context/AuthContext";
import { useRef } from "react";
import { getTrays } from "../../actions";
import ConfirmDialog from "../../components/ConfirmDialog";

const TRANSACTION_FORM_DEFAULTS = {
  stockGiven: false,
  paymentDone: false,
  traysReturned: false,
  discountGiven: false,
  dateOfTransaction: null,
  paymentAmount: "",
  discount: "",
  sales: [
    {
      itemName: "",
      numberOfItems: undefined,
      costPerItem: undefined,
      trayTypeId: undefined,
    },
  ],
  trays: [
    {
      trayTypeId: undefined,
      numberOfTrays: undefined,
      excludePayment: false,
    },
  ],
  labourCharges: {
    enabled: false,
    numberOfItems: undefined,
    costPerItem: undefined,
  },
  damageRefund: {
    enabled: false,
  },
  damageRefunds: [
    {
      selectedSaleIndex: undefined,
      numberOfItems: undefined,
      costPerItem: undefined,
      total: undefined,
    },
  ],
};

/** Transaction Validation Schema */
const transactionSchema = Yup.object({
  stockGiven: Yup.boolean().default(false),
  paymentDone: Yup.boolean().default(false),
  traysReturned: Yup.boolean().default(false),
  discountGiven: Yup.boolean().default(false),

  dateOfTransaction: Yup.date()
    .nullable()
    .when(["stockGiven", "paymentDone", "traysReturned", "discountGiven"], {
      is: (stockGiven, paymentDone, traysReturned, discountGiven) =>
        Boolean(stockGiven) ||
        Boolean(paymentDone) ||
        Boolean(traysReturned) ||
        Boolean(discountGiven),
      then: (schema) =>
        schema
          .required("Date is required")
          .max(new Date(), "Transaction cannot be in future"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),

  sales: Yup.array().when("stockGiven", {
    is: true,
    then: () =>
      Yup.array()
        .of(
          Yup.object({
            itemName: Yup.string().required("Item required"),
            numberOfItems: Yup.number()
              .required("Quantity is required")
              .positive("Quantity must be positive"),
            costPerItem: Yup.number().required("Cost required").positive(),
            trayTypeId: Yup.number(),
          })
        )
        .min(1, "At least one sale required"),
  }),

  trays: Yup.array().when("traysReturned", {
    is: true,
    then: () =>
      Yup.array()
        .of(
          Yup.object({
            numberOfTrays: Yup.number()
              .required("Quantity is required")
              .positive("Quantity must be positive"),
            trayTypeId: Yup.number().required("Tray type is required"),
            excludePayment: Yup.boolean().default(false),
          })
        )
        .min(1, "At least one tray required"),
  }),

  labourCharges: Yup.object({
    enabled: Yup.boolean().default(false),
    numberOfItems: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        if (
          originalValue === "" ||
          originalValue === null ||
          originalValue === undefined
        ) {
          return null;
        }
        const num = Number(originalValue);
        return isNaN(num) ? null : num;
      })
      .when("enabled", {
        is: true,
        then: (schema) =>
          schema.required("Qty is required").positive("Qty must be positive"),
        otherwise: (schema) => schema.nullable(),
      }),
    costPerItem: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        if (
          originalValue === "" ||
          originalValue === null ||
          originalValue === undefined
        ) {
          return null;
        }
        const num = Number(originalValue);
        return isNaN(num) ? null : num;
      })
      .when("enabled", {
        is: true,
        then: (schema) =>
          schema.required("Cost is required").positive("Cost must be positive"),
        otherwise: (schema) => schema.nullable(),
      }),
  }),

  damageRefund: Yup.object({
    enabled: Yup.boolean().default(false),
  }),

  damageRefunds: Yup.array().when("damageRefund.enabled", {
    is: true,
    then: () =>
      Yup.array()
        .of(
          Yup.object({
            selectedSaleIndex: Yup.number().required("Select item"),
            numberOfItems: Yup.number().required("Qty required").positive(),
            costPerItem: Yup.number().required("Cost required").positive(),
          })
        )
        .min(1, "At least one refund required"),
    otherwise: () => Yup.array().nullable().notRequired(), // FIX: Form will now submit if unchecked
  }),

  discount: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return null;
      }
      const num = Number(originalValue);
      return isNaN(num) ? null : num;
    })
    .when("discountGiven", {
      is: true,
      then: (schema) =>
        schema
          .required("Discount amount is required")
          .positive("Discount amount must be positive"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),

  paymentAmount: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      )
        return null;
      const num = Number(originalValue);
      return isNaN(num) ? null : num;
    })
    .when("paymentDone", {
      is: true,
      then: (schema) =>
        schema
          .required("Amount is required")
          .positive("Amount must be positive"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
});

/**Payment Validation Schema */
const paymentSchema = Yup.object({
  amount: Yup.number()
    .typeError("Amount is required")
    .required("Amount is required")
    .positive("Amount must be greater than zero"),
  paymentDate: Yup.date()
    .required("Payment date is required")
    .max(new Date(), "Payment date cannot be in the future"),
  paymentMethod: Yup.string()
    .required("Payment method is required")
    .oneOf(["NEFT", "UPI", "Cash", "Net Banking"], "Invalid payment method"),
});

function CustomerDetails() {
  const [customerDetails, setCustomerDetails] = useState({});
  const [selectedTransactionId, setSelectedTransactionId] = useState("");
  const [open, setOpen] = useState(false);
  const [openPayment, setOpenPayment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [transactionList, setTransactionList] = useState([]);
  const [items, setItems] = useState([]);
  const [loadingTx, setLoadingTx] = useState(false);
  const params = useParams();
  const { id: customerId } = params;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();
  const [openTrayModal, setOpenTrayModal] = useState(false);
  const [excludePayment, setExcludePayment] = useState(false);
  const [editTransaction, setEditTransaction] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptTransaction, setReceiptTransaction] = useState(null);
  const [trayTypes, setTrayTypes] = useState([]);

  const { user } = useAuth();

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  /**Fetch Customer + Transactions */
  useEffect(() => {
    if (!customerId) return;

    setLoadingTx(true);

    const payload = { customerId };

    if (dateRange.startDate && dateRange.endDate) {
      payload.startDate = dayjs(dateRange.startDate)
        .startOf("day").toISOString()
        // .format("YYYY-MM-DD");
      payload.endDate = dayjs(dateRange.endDate)
        .endOf("day").toISOString()
        // .format("YYYY-MM-DD");
    }

    Promise.all([
      axiosInstance.post("/customers/details", { customerId }),
      axiosInstance.post("/transactions/list", payload),
      axiosInstance.get("/stock"),
      getTrays(),
    ])
      .then(([custRes, txRes, stockRes, traysList]) => {
        setCustomerDetails(custRes?.data?.result || {});
        setTransactionList(txRes?.data?.result || []);
        setItems(stockRes?.data?.list || []);
        setTrayTypes(Array.isArray(traysList) ? traysList : []);
      })
      .catch((err) =>
        toast.error(err?.response?.data?.message || "Failed to load data")
      )
      .finally(() => setLoadingTx(false));
  }, [customerId, dateRange, open, params.id]);

  // Delete Customer
  const handleDeleteCustomer = async () => {
    setOpenDeleteDialog(true);
  };

  const customFilterRanges = [
    {
      label: 'This Week', // Today to last Sunday
      value: [dayjs().startOf('week').toDate(), dayjs().toDate()]
    },
    {
      label: 'Last Week', // Last Sun to Last Sat
      value: [
        dayjs().subtract(1, 'week').startOf('week').toDate(),
        dayjs().subtract(1, 'week').endOf('week').toDate()
      ]
    },
    {
      label: 'This Month', // April (Current)
      value: [dayjs().startOf('month').toDate(), dayjs().toDate()]
    },
    {
      label: 'Last Month', // March (Previous)
      value: [
        dayjs().subtract(1, 'month').startOf('month').toDate(),
        dayjs().subtract(1, 'month').endOf('month').toDate()
      ]
    }
  ];

  const confirmDeleteCustomer = async () => {
    try {
      const response = await axiosInstance.delete(`/customers/${customerId}`);
      toast.success(response.data.message || "Customer deleted successfully");
      navigate(-1); // Go back to customers list
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete customer");
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const cancelDeleteCustomer = () => {
    setOpenDeleteDialog(false);
  };

  /** Transaction Form */
  const {
    register,
    watch,
    control,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(transactionSchema),
    defaultValues: TRANSACTION_FORM_DEFAULTS,
  });

  const {
    fields: salesFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "sales",
  });

  const trayForm = useFieldArray({
    control,
    name: "trays",
  });

  const damageRefundForm = useFieldArray({
    control,
    name: "damageRefunds",
  });

  const stockGiven = watch("stockGiven");
  const paymentDone = watch("paymentDone");
  const traysReturned = watch("traysReturned");
  const discountGiven = watch("discountGiven");
  const labourEnabled = watch("labourCharges.enabled");
  const labourCost = watch("labourCharges.costPerItem");
  const labourQty = watch("labourCharges.numberOfItems");
  const damageRefundEnabled = watch("damageRefund.enabled");
  const damageRefunds = watch("damageRefunds") || [];
  const salesValues = watch("sales") || [];
  const watchedTransaction = watch();

  const totalSalesQty = (salesValues || []).reduce(
    (sum, sale) => sum + (Number(sale?.numberOfItems) || 0),
    0
  );

  React.useEffect(() => {
    if (!labourEnabled) return;

    if (totalSalesQty > 0) {
      setValue("labourCharges.numberOfItems", totalSalesQty, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [labourEnabled, totalSalesQty, setValue]);

  React.useEffect(() => {
    if (!discountGiven) {
      setValue("discount", undefined, {
        shouldValidate: false,
        shouldDirty: true,
      });
      clearErrors("discount");
    }
  }, [discountGiven, setValue, clearErrors]);

  React.useEffect(() => {
    if (!paymentDone) {
      setValue("paymentAmount", undefined, {
        shouldValidate: false,
        shouldDirty: true,
      });
      clearErrors("paymentAmount");
    }
  }, [paymentDone, setValue, clearErrors]);

  React.useEffect(() => {
    if (!traysReturned) {
      setValue(
        "trays",
        [
          {
            trayTypeId: undefined,
            numberOfTrays: undefined,
            excludePayment: false,
          },
        ],
        { shouldValidate: false, shouldDirty: true }
      );
      clearErrors("trays");
    }
  }, [traysReturned, setValue, clearErrors]);

  React.useEffect(() => {
    if (!stockGiven) {
      setValue(
        "sales",
        [
          {
            itemName: "",
            trayTypeId: undefined,
            numberOfItems: undefined,
            costPerItem: undefined,
          },
        ],
        { shouldValidate: false, shouldDirty: true }
      );
      clearErrors("sales");
    }
  }, [stockGiven, setValue, clearErrors]);

  React.useEffect(() => {
    // 1. If disabled, clear and exit.
    if (!damageRefundEnabled) {
      // Only clear if there is actually data to avoid unnecessary renders
      if (damageRefunds && damageRefunds.length > 0) {
        setValue("damageRefunds", [], {
          shouldValidate: false,
          shouldDirty: true,
        });
        clearErrors("damageRefunds");
      }
      return;
    }

    // 2. Initial item append if empty
    if (damageRefundEnabled && (!Array.isArray(damageRefunds) || damageRefunds.length === 0)) {
      damageRefundForm.append({
        selectedSaleIndex: undefined,
        numberOfItems: undefined,
        costPerItem: undefined,
        total: undefined,
      });
      return;
    }

    // 3. Loop through refunds and update ONLY if values are different
    damageRefunds.forEach((refund, index) => {
      const selectedSaleIndex = Number.isFinite(Number(refund.selectedSaleIndex))
        ? Number(refund.selectedSaleIndex)
        : undefined;
      
      const selectedSale = Number.isFinite(selectedSaleIndex)
        ? salesValues[selectedSaleIndex]
        : null;

      const selectedTrayType = selectedSale?.trayTypeId && trayTypes.length
        ? trayTypes.find(t => (t.trayTypeId === selectedSale.trayTypeId || t.id === selectedSale.trayTypeId))
        : null;

      // Calculations
      const computedCost = selectedSale
        ? Math.max(Number(selectedSale.costPerItem || 0) - Number(selectedTrayType?.price || 0), 0)
        : undefined;
      
      const qty = Number(refund.numberOfItems || 0);
      const computedTotal = (computedCost !== undefined && qty > 0) ? qty * computedCost : undefined;

      // --- STARK CHANGE DETECTION (Prevents Infinite Loops) ---
      
      // Update Cost Per Item
      if (computedCost !== undefined && Number(refund.costPerItem) !== computedCost) {
        setValue(`damageRefunds.${index}.costPerItem`, computedCost, { shouldValidate: true });
      }

      // Update Total
      if (computedTotal !== undefined && Number(refund.total) !== computedTotal) {
        setValue(`damageRefunds.${index}.total`, computedTotal);
      }

      // Validation: Ensure Qty doesn't exceed original sale items
      if (refund.numberOfItems && selectedSale && qty > Number(selectedSale.numberOfItems || 0)) {
        const maxAllowed = Number(selectedSale.numberOfItems || 0);
        if (Number(refund.numberOfItems) !== maxAllowed) {
          setValue(`damageRefunds.${index}.numberOfItems`, maxAllowed, { shouldValidate: true });
        }
      }
    });

    // Dependency Optimization: 
    // We stringify damageRefunds so the effect only triggers on VALUE changes, 
    // not just because the array reference changed.
  }, [
    damageRefundEnabled, 
    JSON.stringify(damageRefunds), 
    salesValues, 
    trayTypes, 
    setValue, 
    clearErrors
  ]);

  let filteredTrayTypes = [];
  if (Array.isArray(customerDetails?.trayBalance)) {
    filteredTrayTypes = customerDetails.trayBalance.filter(
      (type) => type.count > 0
    );
  }

  /** Payment Form */
  const paymentForm = useForm({
    resolver: yupResolver(paymentSchema),
    mode: "onTouched",
    defaultValues: {
      amount: "",
      paymentDate: null,
      paymentMethod: "",
      referenceId: "",
    },
  });

  const activeTrayTypes = trayTypes.filter((el) => el.status !== false);

  const closeTransactionPopup = () => {
    setEditTransaction(false);
    setSelectedTransaction(null);

    reset(TRANSACTION_FORM_DEFAULTS, {
      keepDirty: false,
      keepTouched: false,
    });
    setExcludePayment(false);
    setOpen(false);
  };

  const paginatedTransactions = transactionList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getTrayBalanceCount = (trayTypeId) => {
    const tray = customerDetails?.trayBalance?.find((t) => t.id === trayTypeId);
    return tray ? Number(tray.count) : 0;
  };

  const selectedTrayTypeIds = watch("trays")
    ?.map((t) => t?.trayTypeId)
    .filter(Boolean);

  const printPDF = () => {
    const printWindow = window.open("", "_blank");
    const val = (v) => v ?? "-";

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Transactions - ${customerDetails?.name || ""} ${
            dateRange?.startDate && dateRange?.endDate
              ? ` - ${new Date(dateRange?.startDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} - ${new Date(dateRange?.endDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`
              : ""
          }</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 30px; color: #333; }
            .header { display: flex; justify-content: space-between; border-bottom: 3px solid #1976d2; padding-bottom: 10px; margin-bottom: 20px; }
            .company-name { font-size: 24px; font-weight: bold; color: #1976d2; }
            .summary-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin-bottom: 20px; background: #f5f5f5; padding: 15px; border-radius: 8px; }
            .summary-item { text-align: center; }
            .summary-label { font-size: 10px; color: #666; text-transform: uppercase; }
            .summary-value { font-size: 14px; font-weight: bold; }
            
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th { background: #f0f0f0; font-weight: bold; border: 1px solid #ccc; padding: 6px; }
            td { border: 1px solid #ccc; padding: 6px; text-align: center; vertical-align: middle; }
            .proprietor-meta { font-size: 13px; color: #555; margin-top: 4px; }
            .tray-column { background-color: #e4ffe4 !important; -webkit-print-color-adjust: exact; }
            .damage-text { background-color: #d32f2f; color: white !important; font-weight: bold; }
            .footer { margin-top: 20px; font-size: 10px; border-top: 1px solid #ccc; padding-top: 10px; display: flex; justify-content: space-between; }
          </style>
        </head>
        <body>
          <div class="header">
            <div style="display: flex; align-items: center; gap: 12px;">
              <img src="${LOGO_BASE64}" style="width: 50px; height: 50px; border-radius: 4px;" />
              <div>
                <div style="font-size: 20px; font-weight: bold; color: #1976d2;">${
                    user?.employerName || ""}
                  </div>
                <div style="color: #666; font-size: 16px;">${
                  customerDetails?.branchName || "Transaction Receipt"
                }</div>
                <div class="proprietor-meta">
                  Proprietor: <strong>Maddireddy Ramesh</strong> &bull; Contact: <strong>+91 76666 77779</strong>
                </div>
              </div>
            </div>
            <div style="text-align: right;">
              <div style="font-size: 20px; font-weight: bold; text-transform: capitalize;">${customerDetails?.name || ""}</div>
              <div style="color: #666; font-size: 14px;">${customerDetails?.mobile || ""} • ${customerDetails?.location || ""}</div>
            </div>
          </div>

          <div class="summary-grid">
            <div class="summary-item"><div class="summary-label">Total Transaction</div><div class="summary-value">₹${grandTotalTransactionAmount.toLocaleString("en-IN")}</div></div>
            <div class="summary-item"><div class="summary-label">Total Discount</div><div class="summary-value">₹${discountAmount.toLocaleString("en-IN")}</div></div>
            <div class="summary-item"><div class="summary-label">Total Damage Refund</div><div class="summary-value" style="color:red">₹${damageRefund.toLocaleString("en-IN")}</div></div>
            <div class="summary-item"><div class="summary-label">Total Amount Paid</div><div class="summary-value" style="color:green">₹${(grandTotalPaidAmount + grandTrayPaymentAmount).toLocaleString("en-IN")}</div></div>
            <div class="summary-item">
              <div class="summary-label">Total Amount Due</div>
              <div class="summary-value" style="color:#1976d2">
                ₹${(grandTotalTransactionAmount + labourCharges - (grandTotalPaidAmount + grandTrayPaymentAmount + discountAmount + damageRefund)).toLocaleString("en-IN")}
              </div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th rowspan="2">Date</th>
                <th colspan="5">Sales</th>
                <th colspan="3" class="tray-column">Trays Returned</th>
                <th rowspan="2" style="background:#fff9e6">Discount</th>
                <th colspan="3">Payment</th>
                <th rowspan="2">By</th>
              </tr>
              <tr>
                <th>Item</th><th>Qty</th><th>Price</th><th>Total</th><th>Loading</th>
                <th class="tray-column">Qty</th><th class="tray-column">Price</th><th class="tray-column">Total</th>
                <th>Cash</th><th>Tray</th><th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${transactionList.map((tx) => {
                  let salesRows = tx.sales ? [...tx.sales] : [];
                  if (tx?.damageRefunds?.length > 0) {
                      const tempDamage = tx.damageRefunds.map(d => ({ ...d, isDamage: true }));
                      salesRows = [...salesRows, ...tempDamage];
                  }

                  const trays = tx.trays || [];
                  const rowCount = Math.max(salesRows.length, trays.length, 1);
                  const pAmt = Number(tx.paymentAmount || 0);
                  const tTotal = trays.reduce((s, t) => s + (Number(t.numberOfTrays || 0) * Number(t.costPerTray || 0)), 0);

                  return Array.from({ length: rowCount }).map((_, i) => {
                      const sale = salesRows[i];
                      const tray = trays[i];
                      return `
                          <tr>
                              ${i === 0 ? `
                              <td rowspan="${rowCount}">
                                ${tx.dateOfTransaction ? new Date(tx.dateOfTransaction).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}
                              </td>` : ""}
                              
                              <td class="${sale?.isDamage ? 'damage-text' : ''}">${val(sale?.itemName)}</td>
                              <td class="${sale?.isDamage ? 'damage-text' : ''}">${val(sale?.numberOfItems)}</td>
                              <td class="${sale?.isDamage ? 'damage-text' : ''}">${val(sale?.costPerItem)}</td>
                              <td class="${sale?.isDamage ? 'damage-text' : ''}">${sale?.isDamage ? '-' : ''}${val(sale?.total)}</td>
                              
                              ${i === 0 ? `<td rowspan="${rowCount}">${val(tx.labourCharges?.total)}</td>` : ""}

                              <td class="tray-column">${val(tray?.numberOfTrays)}</td>
                              <td class="tray-column">${val(tray?.costPerTray)}</td>
                              <td class="tray-column">${tray ? (tray.numberOfTrays * tray.costPerTray) : "-"}</td>

                              ${i === 0 ? `
                                <td rowspan="${rowCount}" style="background:#fff9e6;font-weight:bold;color:#b45309">${tx.discount ? Number(tx.discount).toLocaleString('en-IN') : '-'}</td>
                                  <td rowspan="${rowCount}">${pAmt}</td>
                                  <td rowspan="${rowCount}">${tTotal}</td>
                                  <td rowspan="${rowCount}" style="font-weight:bold">${pAmt + tTotal}</td>
                                  <td rowspan="${rowCount}">${tx.transactionBy?.fullName || "-"}</td>
                              ` : ""}
                          </tr>
                      `;
                  }).join("");
              }).join("")}
            </tbody>
          </table>
        </body>
      </html>
    `);

  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
};
  const closePaymentPopup = () => {
    paymentForm.reset();
    setOpenPayment(false);
  };

  const clearDateFilter = () => {
    setDateRange({
      startDate: null,
      endDate: null,
    });
  };

  const formatCurrency = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  /** Add Transaction */
  const addTransaction = async (data) => {
    let dateOfTransaction = data.dateOfTransaction;

    // If no date is provided, use current date as fallback
    if (!dateOfTransaction) {
      dateOfTransaction = new Date();
    }

    // Build payload with all checkbox states and their corresponding fields
    const payload = {
      customerId: params?.id,
      dateOfTransaction: dateOfTransaction,
      paymentAmount: data?.paymentAmount,
    };

    if (stockGiven) {
      payload.sales = data?.sales;
    }

    if (data?.labourCharges?.enabled) {
      payload.labourCharges = {
        enabled: true,
        numberOfItems: totalSalesQty,
        costPerItem: data.labourCharges.costPerItem,
        total:
          Number(totalSalesQty || 0) *
          Number(data.labourCharges.costPerItem || 0),
      };
    }

    if (traysReturned) {
      payload.trays = data.trays.map((tray) => {
        const selectedTrayType = trayTypes.find(
          (type) =>
            type.trayTypeId === tray.trayTypeId || type.id === tray.trayTypeId
        );
        tray.costPerTray = excludePayment ? 0 : selectedTrayType?.price || 0;
        tray.excludePayment = excludePayment;
        return tray;
      });
    }

    if (discountGiven) {
      payload.discount = data.discount;
    }

    if (editTransaction && data?.damageRefund?.enabled) {
      const refundRows = Array.isArray(data.damageRefunds)
        ? data.damageRefunds
        : [];
      const damageRefundsPayload = [];

      refundRows.forEach((refund) => {
        const rowIndex = Number(refund.selectedSaleIndex);
        const selectedSale = Array.isArray(data.sales)
          ? data.sales[rowIndex]
          : null;
        const selectedTrayType = selectedSale?.trayTypeId
          ? trayTypes.find(
              (type) =>
                type.trayTypeId === selectedSale.trayTypeId ||
                type.id === selectedSale.trayTypeId
            )
          : null;
        const refundCost = Math.max(
          Number(selectedSale?.costPerItem || 0) -
            Number(selectedTrayType?.price || 0),
          0
        );
        const refundQty = Number(refund.numberOfItems || 0);

        if (selectedSale && refundQty > 0 && refundCost > 0) {
          damageRefundsPayload.push({
            itemName: selectedSale.itemName || "",
            selectedSaleIndex: rowIndex,
            numberOfItems: refundQty,
            costPerItem: refundCost,
            total: refundQty * refundCost,
          });
        }
      });

      if (damageRefundsPayload.length > 0) {
        payload.damageRefunds = damageRefundsPayload;
      }
    }

    let path = "transactions/create";

    if (editTransaction) {
      path = "transactions/update/" + selectedTransaction._id;
    }

    try {
      setSaving(true);
      const response = await axiosInstance.post(path, payload);
      toast.success(
        response?.data?.message || "Transaction saved successfully"
      );

      // Show receipt only for new transactions (not edits)
      if (!editTransaction) {
        setReceiptTransaction(response?.data?.transaction);
        setShowReceipt(true);
      }

      closeTransactionPopup();
      setSaving(false);
    } catch (err) {
      setSaving(false);
      console.error("Error submitting transaction:", err); // Debug log
      toast.error(err?.response?.data?.message || "Failed to save transaction");
    }
  };

  const val = (v) => v ?? "-";

  /*Update Payment */
  const updatePayment = async (data) => {
    const payload = {
      transactionId: selectedTransactionId,
      amount: data.amount,
      paymentDate: data.paymentDate,
      paymentMethod: data.paymentMethod,
      referenceId: data.referenceId,
    };

    try {
      const response = await axiosInstance.post(
        "transactions/update-payment",
        payload
      );
      toast.success(response?.data?.message || "Payment updated successfully");
      closePaymentPopup();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update payment");
    }
  };

  const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const totalTransactionAmount = paginatedTransactions.reduce((sum, tx) => {
    const salesTotal = (tx.sales || []).reduce((tSum, sale) => {
      return tSum + toNumber(sale?.total);
    }, 0);
    return sum + salesTotal;
  }, 0);

  const discountAmount = transactionList.reduce((sum, tx) => {
    return sum + (toNumber(tx?.discount) || 0);
  }, 0);

  const grandTotalTransactionAmount = transactionList.reduce((sum, tx) => {
    const salesTotal = (tx.sales || []).reduce((tSum, sale) => {
      return tSum + toNumber(sale?.total);
    }, 0);
    return sum + salesTotal;
  }, 0);

  const damageRefund = transactionList.reduce((sum, tx) => {
    const damageTotal = (tx.damageRefunds || [tx.damageRefund] || []).reduce(
      (tSum, damage) => {
        return tSum + toNumber(damage?.total);
      },
      0
    );
    return sum + damageTotal;
  }, 0);

  const labourCharges = transactionList.reduce((sum, tx) => {
    return sum + toNumber(tx?.labourCharges?.total || 0);
  }, 0);

  const totalPaidAmount = paginatedTransactions.reduce((sum, tx) => {
    return sum + toNumber(tx?.paymentAmount);
  }, 0);

  const grandTotalPaidAmount = transactionList.reduce((sum, tx) => {
    return sum + toNumber(tx?.paymentAmount);
  }, 0);

  const trayPaymentAmount = paginatedTransactions.reduce((sum, tx) => {
    const traysTotal = (tx.trays || []).reduce((tSum, tray) => {
      if (excludePayment) return tSum;

      return tSum + toNumber(tray?.numberOfTrays) * toNumber(tray?.costPerTray);
    }, 0);
    return sum + traysTotal;
  }, 0);

  const grandTrayPaymentAmount = transactionList.reduce((sum, tx) => {
    const traysTotal = (tx.trays || []).reduce((tSum, tray) => {
      return tSum + toNumber(tray?.numberOfTrays) * toNumber(tray?.costPerTray);
    }, 0);

    return sum + traysTotal;
  }, 0);

  const generateReceiptHTML = (tx, customerDetails, accountBalanceData) => {
    const sales = tx.sales || [];
    const trays = tx.trays || [];
    const labourCharges = tx.labourCharges || {};
    const damageRefunds =
      Array.isArray(tx.damageRefunds) && tx.damageRefunds.length > 0
        ? tx.damageRefunds
        : tx.damageRefund?.enabled
        ? [tx.damageRefund]
        : [];
    const paymentReceived = Number(tx.paymentAmount) || 0;

    const salesTotal = sales.reduce(
      (sum, s) => sum + (Number(s.numberOfItems) * Number(s.costPerItem) || 0),
      0
    );
    const labourTotal = Number(
      labourCharges.enabled ? labourCharges.total || 0 : 0
    );
    const refundAmount = damageRefunds.reduce(
      (sum, refund) => sum + Number(refund.total || 0),
      0
    );
    const discountAmount = Number(tx.discount || 0);
    const trayPaymentTotal = trays.reduce(
      (sum, t) => sum + (Number(t.numberOfTrays) * Number(t.costPerTray) || 0),
      0
    );
    const totalCredits = paymentReceived + trayPaymentTotal;
    const transactionBalance =
      salesTotal + labourTotal - refundAmount - totalCredits - discountAmount;
    const accountBalance = accountBalanceData?.totalAmountDue || 0;

    return `
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .receipt-container { border: 1px solid #ccc; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            th, td { border: 1px solid #333; padding: 8px; }
            .primary-color { color: #1976d2; }
            .right { text-align: right; }
            /* Add the rest of your receipt styles here */
          </style>
        </head>
        <body>
          <div class="receipt-container">
            <h2>${user?.employerName}</h2>
            <p>Customer: ${customerDetails?.name}</p>
            <p>Date: ${new Date(tx.dateOfTransaction).toLocaleDateString(
              "en-IN"
            )}</p>
            
            <table>
              <thead><tr><th>Item</th><th class="right">Qty</th><th class="right">Amount</th></tr></thead>
              <tbody>
                ${sales
                  .map(
                    (s) =>
                      `<tr><td>${s.itemName}</td><td class="right">${s.numberOfItems}</td><td class="right">₹${s.total}</td></tr>`
                  )
                  .join("")}
                ${
                  labourCharges.enabled
                    ? `<tr><td>Loading Charges</td><td class="right">${labourCharges.numberOfItems}</td><td class="right">₹${labourTotal}</td></tr>`
                    : ""
                }
                ${damageRefunds
                  .map(
                    (refund) =>
                      `<tr><td>Damage Refund: ${refund.itemName}</td><td class="right">${refund.numberOfItems}</td><td class="right">- ₹${refund.total}</td></tr>`
                  )
                  .join("")}
              </tbody>
            </table>

            <div class="right">
              <p><strong>Total Due: ₹${
                salesTotal + labourTotal - refundAmount
              }</strong></p>
              ${
                discountAmount > 0
                  ? `<p><strong>Discount: ₹${discountAmount}</strong></p>`
                  : ""
              }
              <p><strong>Total Paid: ₹${totalCredits}</strong></p>
              <hr/>
              <p><strong>Balance: ₹${transactionBalance}</strong></p>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const openEditTransactionPopup = (tx) => {
    setEditTransaction(true);
    setSelectedTransaction(tx);

    const hasSales = Array.isArray(tx.sales) && tx.sales.length > 0;
    const hasTrays = Array.isArray(tx.trays) && tx.trays.length > 0;
    const hasPayment = !!tx.paymentAmount;
    const hasDiscount = !!tx.discount;

    let trayPaymentExcluded = tx.trays.some((t) => t.excludePayment);
    const damageRefundItemIndex =
      hasSales && tx.damageRefund?.itemName
        ? tx.sales.findIndex(
            (sale) => sale.itemName === tx.damageRefund.itemName
          )
        : -1;

    setExcludePayment(trayPaymentExcluded);

    reset({
      stockGiven: hasSales,
      traysReturned: hasTrays,
      discountGiven: hasDiscount,
      paymentDone: hasPayment,

      dateOfTransaction: tx.dateOfTransaction
        ? new Date(tx.dateOfTransaction)
        : null,

      paymentAmount: tx.paymentAmount || "",
      discount: tx.discount || "",

      sales: hasSales
        ? tx.sales.map((s) => ({
            itemName: s.itemName,
            numberOfItems: s.numberOfItems,
            costPerItem: s.costPerItem,
            trayTypeId: s.trayTypeId ?? undefined,
          }))
        : [
            {
              itemName: "",
              numberOfItems: undefined,
              costPerItem: undefined,
              trayTypeId: undefined,
            },
          ],

      labourCharges: tx.labourCharges
        ? {
            enabled: Boolean(tx.labourCharges.enabled),
            numberOfItems: tx.labourCharges.numberOfItems,
            costPerItem: tx.labourCharges.costPerItem,
          }
        : {
            enabled: false,
            numberOfItems: undefined,
            costPerItem: undefined,
          },

      damageRefund: {
        enabled: Boolean(
          (Array.isArray(tx.damageRefunds) && tx.damageRefunds.length > 0) ||
            tx.damageRefund?.enabled
        ),
      },

      damageRefunds:
        Array.isArray(tx.damageRefunds) && tx.damageRefunds.length > 0
          ? tx.damageRefunds.map((refund) => ({
              selectedSaleIndex:
                typeof refund.selectedSaleIndex === "number"
                  ? refund.selectedSaleIndex
                  : damageRefundItemIndex >= 0
                  ? damageRefundItemIndex
                  : undefined,
              numberOfItems: refund.numberOfItems,
              costPerItem: refund.costPerItem,
              total: refund.total,
            }))
          : tx.damageRefund?.enabled
          ? [
              {
                selectedSaleIndex:
                  damageRefundItemIndex >= 0
                    ? damageRefundItemIndex
                    : undefined,
                numberOfItems: tx.damageRefund.numberOfItems,
                costPerItem: tx.damageRefund.costPerItem,
                total: tx.damageRefund.total,
              },
            ]
          : [
              {
                selectedSaleIndex: undefined,
                numberOfItems: undefined,
                costPerItem: undefined,
                total: undefined,
              },
            ],

      trays: hasTrays
        ? tx.trays.map((t) => ({
            numberOfTrays: t.numberOfTrays,
            trayTypeId: t.trayTypeId,
            excludePayment: t.excludePayment,
          }))
        : [
            {
              numberOfTrays: undefined,
              trayTypeId: undefined,
              excludePayment: false,
            },
          ],
    });

    setOpen(true);
  };

  const shareTransactionToWhatsapp = (tx) => {
    const sales = tx.sales || [];
    const trays = tx.trays || [];
    const paymentAmount = Number(tx.paymentAmount) || 0;

    const salesTotal = sales.reduce(
      (sum, sale) =>
        sum + (Number(sale.numberOfItems) * Number(sale.costPerItem) || 0),
      0
    );

    const trayPaymentTotal = trays.reduce((sum, tray) => {
      if (tray.excludePayment) return sum;

      const trayType =
        trayTypes.find(
          (t) => t.trayTypeId === tray.trayTypeId || t.id === tray.trayTypeId
        ) || null;
      const cost = trayType?.price || Number(tray.costPerTray) || 0;
      return sum + Number(tray.numberOfTrays) * cost;
    }, 0);

    const totalCredits = paymentAmount + trayPaymentTotal;
    const transactionBalance = salesTotal - totalCredits;
    const receiptUrl = `${window.location.origin}/receipt/${tx._id}`;

    const message = `*Transaction Receipt*\n\nCustomer: ${
      customerDetails?.name || "N/A"
    }\nDate: ${new Date(tx.dateOfTransaction).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    })}\nBranch: ${
      customerDetails?.branchName || customerDetails?.address || "N/A"
    }\n\n*Summary:*\n💰 Sales Total: ₹${salesTotal.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}\n✅ Credits: ₹${totalCredits.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}\n📊 Balance: ₹${Math.abs(transactionBalance).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}\n\n🔗 View Receipt: ${receiptUrl}\n\nThank you!`;

    const mobile = customerDetails?.mobile || "";
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl =
      mobile && mobile !== "0000000000"
        ? `https://api.whatsapp.com/send?phone=91${mobile}&text=${encodedMessage}`
        : `https://api.whatsapp.com/send?text=${encodedMessage}`;

    window.open(whatsappUrl, "_blank");
  };

  const shareTransactions = () => {
    const sDate = dateRange.startDate ? dayjs(dateRange.startDate).format("YYYY-MM-DD") : "";
    const eDate = dateRange.endDate ? dayjs(dateRange.endDate).format("YYYY-MM-DD") : "";
  
    const listUrl = `${window.location.origin}/transactions/anonomous?startDate=${sDate}&endDate=${eDate}&customerId=${params?.id}`;
    const message = `*Transactions ${dateRange?.startDate && dateRange?.endDate ? `- ${dateRange.startDate} to ${dateRange.endDate}` : ""}*\n\nCustomer: ${
      customerDetails?.name || "N/A"
    }\nBranch: ${
      customerDetails?.branchName || customerDetails?.address || "N/A"
    }\n\n🔗 View Transactions: ${listUrl}\n\nThank you!`;

    const mobile = customerDetails?.mobile || "";
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl =
      mobile && mobile !== "0000000000"
        ? `https://api.whatsapp.com/send?phone=91${mobile}&text=${encodedMessage}`
        : `https://api.whatsapp.com/send?text=${encodedMessage}`;

    window.open(whatsappUrl, "_blank");
  }

  const printTransaction = (tx) => {
    // 1. Data Parsing & Calculations (Sync with React Logic)
    const sales = tx.sales || [];
    const trays = tx.trays || [];
    const paymentAmount = Number(tx.paymentAmount) || 0;
    const discount = Number(tx.discount) || 0;

    const salesTotal = sales.reduce(
      (sum, sale) =>
        sum + (Number(sale.numberOfItems) * Number(sale.costPerItem) || 0),
      0
    );

    const trayPaymentTotal = trays.reduce((sum, tray) => {
      if (tray.excludePayment) return sum;
      const trayType =
        trayTypes.find(
          (t) => t.trayTypeId === tray.trayTypeId || t.id === tray.trayTypeId
        ) || null;
      const cost = trayType?.price || Number(tray.costPerTray) || 0;
      return sum + Number(tray.numberOfTrays) * cost;
    }, 0);

    const currentLabourTotal = tx.labourCharges?.enabled 
      ? Number(tx.labourCharges.total || 0) 
      : 0;

    const totalCredits = paymentAmount + trayPaymentTotal;
    const transactionBalance = (salesTotal + currentLabourTotal) - totalCredits - discount;

    // Note: Ensure grandTotal variables are accessible in your scope or passed in
    const accountBalance =
      grandTotalTransactionAmount +
      labourCharges -
      (grandTotalPaidAmount +
        grandTrayPaymentAmount +
        discountAmount +
        damageRefund);

    // 2. Formatting Helpers
    const formatCurrency = (amount) =>
      Number(amount || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    const formatDate = (date) =>
      new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });

    const primaryColor = "#1976d2";
    const successColor = "#2e7d32";
    const errorColor = "#d32f2f";

    // 3. Generate Print Window
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${customerDetails?.name} - ${formatDate(
      tx.dateOfTransaction
    )}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; padding: 20px; font-size: 14px; color: #333; }
        .receipt-container { border: 1px solid #ccc; padding: 20px; max-width: 800px; margin: auto; }
        
        table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
        th, td { border: 1px solid #333; padding: 8px; }
        
        .section-header { 
          background: ${primaryColor} !important; 
          color: white !important; 
          text-align: center; 
          font-weight: bold; 
          padding: 10px;
        }
        
        .payment-header { background: ${successColor} !important; color: white !important; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .bold { font-weight: bold; }
        .proprietor-meta { font-size: 13px; color: #555; margin-top: 4px; }
        
        @media print {
          body { padding: 10px; }
          .receipt-container { border: none; }
        }
      </style>
    </head>

    <body>
      <div class="receipt-container">
        <div style="display: flex; justify-content: space-between; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid ${primaryColor};">
          <div style="display: flex; align-items: center; gap: 12px;">
            <img src="${LOGO_BASE64}" style="width: 50px; height: 50px; border-radius: 4px;" />
            <div>
              <div style="font-size: 20px; font-weight: bold; color: ${primaryColor};">${
                  user?.employerName || ""}
                </div>
              <div style="color: #666; font-size: 16px;">${
                customerDetails?.branchName || "Transaction Receipt"
              }</div>
              <div class="proprietor-meta">
                Proprietor: <strong>Maddireddy Ramesh</strong> &bull; Contact: <strong>+91 76666 77779</strong>
              </div>
            </div>
          </div>
          <div style="text-align: right;">
            <div style="color: ${primaryColor}; font-weight: 500;">
              <span style="color: #999;">Date:</span> ${formatDate(
                tx.dateOfTransaction
              )}
            </div>
          </div>
        </div>

        <div style="margin-bottom: 20px;">
          <div style="font-size: 20px; font-weight: bold; color: ${primaryColor}; text-transform: capitalize;">
            ${customerDetails?.name || "N/A"}
          </div>
          <div style="text-transform: capitalize; color: #666;">
            ${customerDetails?.location || "N/A"}
          </div>
        </div>

        ${
          sales.length > 0
            ? `
        <table>
          <thead>
            <tr><th colspan="5" class="section-header">Sales</th></tr>
            <tr style="background: #f5f5f5;">
              <th style="width: 40px;">#</th>
              <th>Item</th>
              <th class="text-right" style="width: 80px;">Qty</th>
              <th class="text-right" style="width: 100px;">Rate</th>
              <th class="text-right" style="width: 120px;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${sales
              .map(
                (s, i) => `
              <tr>
                <td class="text-center">${i + 1}</td>
                <td>${s.itemName}</td>
                <td class="text-right">${s.numberOfItems}</td>
                <td class="text-right">₹${formatCurrency(s.costPerItem)}</td>
                <td class="text-right">₹${formatCurrency(
                  Number(s.numberOfItems) * Number(s.costPerItem)
                )}</td>
              </tr>
            `
              )
              .join("")}
            <tr style="background: #f9f9f9; font-weight: bold;">
              <td colspan="4" class="text-right">Loading Charges Total:</td>
              <td class="text-right">₹${formatCurrency(currentLabourTotal)}</td>
            </tr>
            <tr style="background: #f9f9f9; font-weight: bold;">
              <td colspan="4" class="text-right">Total:</td>
              <td class="text-right">₹${formatCurrency(salesTotal + currentLabourTotal)}</td>
            </tr>
          </tbody>
        </table>
        `
            : ""
        }

        ${
          paymentAmount > 0 || trays.length > 0
            ? `
        <table>
          <thead>
            <tr><th colspan="4" class="section-header payment-header">Payment and Tray Returns</th></tr>
          </thead>
          <tbody>
            ${
              paymentAmount > 0
                ? `
            <tr style="background: #e8f5e9;">
              <td colspan="3" class="bold">Cash/Online</td>
              <td class="text-right bold" style="color: ${successColor};">₹${formatCurrency(
                    paymentAmount
                  )}</td>
            </tr>`
                : ""
            }
            ${trays
              .map((t) => {
                const type = trayTypes.find(
                  (tt) =>
                    tt.trayTypeId === t.trayTypeId || tt.id === t.trayTypeId
                );
                const price = type?.price || 0;
                const displayPrice = t.excludePayment
                  ? "0 (Payment Excluded)"
                  : formatCurrency(price);
                const rowTotal = t.excludePayment ? 0 : t.numberOfTrays * price;
                return `
              <tr style="background: #e8f5e9;">
                <td class="bold">Tray Returned</td>
                <td>${type?.label || "Tray"}</td>
                <td class="text-center">${
                  t.numberOfTrays
                } × ₹${displayPrice}</td>
                <td class="text-right bold" style="color: ${successColor};">₹${formatCurrency(
                  rowTotal
                )}</td>
              </tr>`;
              })
              .join("")}
            <tr style="background: #c8e6c9; font-weight: bold;">
              <td colspan="3" class="text-right">Total Payment Received:</td>
              <td class="text-right" style="color: ${successColor}; font-size: 16px;">₹${formatCurrency(
                totalCredits
              )}</td>
            </tr>
          </tbody>
        </table>
        `
            : ""
        }

        <table>
          <tbody>
            <tr style="background: #f5f5f5;">
              <td class="bold">Sales Total</td>
              <td class="text-right bold">₹${formatCurrency(salesTotal + currentLabourTotal)}</td>
            </tr>
            ${
              totalCredits > 0
                ? `
            <tr style="background: #e8f5e9;">
              <td class="bold">Total Credits</td>
              <td class="text-right bold" style="color: ${successColor};">- ₹${formatCurrency(
                    totalCredits
                  )}</td>
            </tr>`
                : ""
            }
            ${
              discount > 0
                ? `
            <tr style="background: #e8f5e9;">
              <td class="bold">Discount</td>
              <td class="text-right bold" style="color: ${successColor};">{formatCurrency(
                    discount
                  )}</td>
            </tr>`
                : ""
            }
            <tr style="background: #e3f2fd;">
              <td class="bold" style="font-size: 16px;">
                ${transactionBalance >= 0 ? "Balance Due" : "Credit Balance"}
              </td>
              <td class="text-right bold" style="font-size: 18px; color: ${
                transactionBalance >= 0 ? errorColor : successColor
              };">
                ₹${formatCurrency(Math.abs(transactionBalance))}
              </td>
            </tr>
          </tbody>
        </table>

        <div style="text-align: center; margin-top: 20px; padding-top: 15px; border-top: 2px dashed #ccc; font-size: 18px;">
          <span style="font-weight: bold;">Overall Account Balance:</span>
          <span style="font-weight: bold; margin-left: 10px; color: ${
            accountBalance > 0 ? errorColor : successColor
          };">
            ₹${formatCurrency(Math.abs(accountBalance))} ${
      accountBalance > 0 ? "(Due)" : "(Credit)"
    }
          </span>
        </div>

      </div>
    </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <>
      {/* Customer Summary */}
      {customerDetails && (
        <Paper
          elevation={2}
          sx={{ p: 2, mb: 0, borderRadius: 0, backgroundColor: "#fafafa" }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Stack
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={1}
              >
                <IconButton onClick={() => navigate(-1)} sx={{ p: 0 }}>
                  <ArrowBack />
                </IconButton>
                <Typography variant="h5" fontWeight="bold">
                  {customerDetails?.name || "—"}
                </Typography>
              </Stack>
              <Stack
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={3}
              >
                <Typography
                  color="text.secondary"
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <MdPhoneAndroid size={18} />
                  {customerDetails?.mobile || "N/A"}
                </Typography>
                <Typography
                  color="text.secondary"
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <MdLocationOn size={18} />
                  {customerDetails?.location || "N/A"}
                </Typography>

                {/* {user?.roleId !== 3 && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDeleteCustomer}
                    size="small"
                    startIcon={<FaTrash />}
                  >
                    Delete Customer
                  </Button>
                )} */}
              </Stack>
            </Box>
            <Box textAlign="right" display={"flex"} gap={4}>
              <Box textAlign="right">
                <Typography variant="subtitle2" color="text.secondary">
                  Total Transaction Value
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  ₹ {grandTotalTransactionAmount.toLocaleString("en-IN")}
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="subtitle2" color="text.secondary">
                  Total Discount
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  ₹ {discountAmount.toLocaleString("en-IN")}
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="subtitle2" color="text.secondary">
                  Total Damage Refund
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="red">
                  ₹ {damageRefund.toLocaleString("en-IN")}
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="subtitle2" color="text.secondary">
                  Total Amount Paid
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="green">
                  ₹{" "}
                  {(
                    grandTotalPaidAmount + grandTrayPaymentAmount
                  ).toLocaleString("en-IN")}
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="subtitle2" color="text.secondary">
                  Total Amount Due
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  ₹{" "}
                  {(
                    grandTotalTransactionAmount +
                    labourCharges -
                    (grandTotalPaidAmount +
                      grandTrayPaymentAmount +
                      discountAmount +
                      damageRefund)
                  ).toLocaleString("en-IN")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}
      <Box sx={{ p: 2, py: 0 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          my={2.5}
        >
          <Typography variant="h6" fontWeight="bold">
            Transactions
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <Button
              variant="contained"
              onClick={() => setOpenTrayModal(true)}
              size="small"
            >
              Tray Balance
            </Button>

            <Button
              variant="contained"
              onClick={() => navigate("/tray-history/" + params?.id)}
              size="small"
            >
              Tray History
            </Button>
            
            <RangeFilter 
              dateRange={dateRange} 
              setDateRange={setDateRange} 
              width={200}
              ranges={customFilterRanges}
            />

            <IconButton
              onClick={printPDF}
              disabled={paginatedTransactions.length === 0}
              sx={{
                color: "primary.main",
                // border: "1px solid",
                // borderColor: "primary.main",
                // borderRadius: "4px",
                padding: "5px",
                "&.Mui-disabled": {
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                },
              }}
            >
              <FaPrint size={22} />
            </IconButton>

            <IconButton 
              onClick={shareTransactions}
              disabled={paginatedTransactions.length === 0}
              sx={{ 
                color: '#25D366', 
                // border: '1px solid #25D366', 
                // borderRadius: '4px',
                padding: '5px',
                '&:hover': {
                  backgroundColor: '#f0fff4',
                  // borderColor: '#128C7E'
                }
              }}
            >
              <FaWhatsapp size={22} />
            </IconButton>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Add New
            </Button>
          </Box>
        </Box>

        {/* Table */}

        {loadingTx ? (
          <Box display="flex" flexDirection="column" alignItems="center" my={4}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading transactions...
            </Typography>
          </Box>
        ) : transactionList.length === 0 ? (
          <Box display="flex" justifyContent="center" my={2}>
            <Typography
              color="text.secondary"
              variant="h5"
              fontWeight="bold"
              sx={{
                background: "#fafafa",
                py: "40px",
                display: "block",
                width: "100%",
                textAlign: "center",
              }}
            >
              No transactions found.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 0,
                border: "1px solid #e0e0e0",
                maxHeight: "calc(100vh - 227px)",
                overflow: "auto",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  {/* Group Header Row */}
                  <TableRow>
                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        verticalAlign: "middle",
                        borderRight: "2px solid #e0e0e0",
                        py: 1,
                        backgroundColor: "#ffffff",
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <strong>Date</strong>
                    </TableCell>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <strong>Sales</strong>
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        backgroundColor: "#e4ffe4",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <strong>Trays Returned</strong>
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        verticalAlign: "middle",
                        py: 0.2,
                        backgroundColor: "#fff9e6",
                        borderRight: "2px solid #e0e0e0",
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <strong>Discount</strong>
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        backgroundColor: "#f5f5f5",
                        py: 0.2,
                        borderRight: "2px solid #e0e0e0",
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <strong>Payment</strong>
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        verticalAlign: "middle",
                        py: 0.2,
                        backgroundColor: "#ffffff",
                        borderRight: "2px solid #e0e0e0",
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <strong>Transaction By</strong>
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        verticalAlign: "middle",
                        py: 0.2,
                        backgroundColor: "#ffffff",
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                  {/* Column Header Row */}
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Item</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Qty</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Price</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Loading</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#e4ffe4",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Qty</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#e4ffe4",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Price</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#e4ffe4",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        py: 0.2,
                        borderRight: "2px solid #e0e0e0",
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Payment Received</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        py: 0.2,
                        borderRight: "2px solid #e0e0e0",
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Tray Payment</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Total</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTransactions.map((tx) => {
                    let tempDamage = [];
                    if (tx?.damageRefunds?.length > 0) {
                      tempDamage = tx.damageRefunds.map((item) => ({
                        ...item,
                        isDamage: true,
                      }));
                    } else if (tx?.damageRefund?.enabled) {
                      tempDamage = [{ ...tx.damageRefund, isDamage: true }];
                    }
                    let sales = tx.sales ? [...tx.sales] : [];

                    if (tx.labourCharges?.enabled) {
                      sales.push({
                        itemName: "Loading Charges",
                        numberOfItems: tx.labourCharges.numberOfItems,
                        costPerItem: tx.labourCharges.costPerItem,
                        total: tx.labourCharges.total,
                        isLabour: true, // Flag for styling
                      });
                    }
                    sales = [...tx.sales, ...tempDamage] || [];
                    const trays = tx.trays || [];

                    const rowCount = Math.max(sales.length, trays.length, 1);
                    const paymentAmount = tx.paymentAmount
                      ? tx.paymentAmount
                      : 0;

                    const traysTotal = (tx.trays || []).reduce((sum, t) => {
                      const qty = toNumber(t?.numberOfTrays);
                      const cost = toNumber(t?.costPerTray);
                      return sum + qty * cost;
                    }, 0);
                    const grandTotal = paymentAmount + traysTotal;

                    return (
                      <>
                        {Array.from({ length: rowCount }).map((_, index) => {
                          const tray = tx.trays?.[index];
                          const sale = sales[index];

                          const trayTotal =
                            tray?.numberOfTrays != null &&
                            tray?.costPerTray != null
                              ? tray.numberOfTrays * tray.costPerTray
                              : "-";

                          return (
                            <TableRow key={index}>
                              {index === 0 && (
                                <TableCell
                                  rowSpan={rowCount}
                                  sx={{ borderRight: "2px solid #e0e0e0" }}
                                >
                                  {new Date(
                                    tx.dateOfTransaction
                                  ).toLocaleDateString("en-IN", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    timeZone: "Asia/Kolkata",
                                  })}
                                </TableCell>
                              )}

                              {/* SALES */}
                              <TableCell
                                className={sale?.isDamage ? "demage" : ""}
                                sx={{
                                  borderRight: "2px solid #e0e0e0"
                                }}
                              >
                                {val(sale?.itemName)}{" "}
                                 
                              </TableCell>
                              <TableCell
                                className={sale?.isDamage ? "demage" : ""}
                                sx={{
                                  borderRight: "2px solid #e0e0e0",
                                }}
                              >
                                {val(sale?.numberOfItems)}
                              </TableCell>
                              <TableCell
                                className={sale?.isDamage ? "demage" : ""}
                                sx={{
                                  borderRight: "2px solid #e0e0e0",
                                }}
                              >
                                {val(sale?.costPerItem)}
                              </TableCell>
                              <TableCell
                                className={sale?.isDamage ? "demage" : ""}
                                sx={{
                                  borderRight: "2px solid #e0e0e0",
                                }}
                              >
                                {val(sale?.total)}
                              </TableCell>
                              <TableCell
                                rowSpan={index === 0 ? rowCount : 1}
                                sx={{
                                  borderRight: "2px solid #e0e0e0",
                                  display: index === 0 ? "table-cell" : "none",
                                  verticalAlign: "middle",
                                  backgroundColor: "inherit",
                                }}
                              >
                                {index === 0 && tx.labourCharges?.enabled
                                  ? tx.labourCharges.total
                                  : "-"}
                              </TableCell>

                              {/* TRAYS */}
                              <TableCell
                                sx={{
                                  borderRight: "2px solid #e0e0e0",
                                  background: "#e4ffe4",
                                }}
                              >
                                {val(tray?.numberOfTrays)}
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderRight: "2px solid #e0e0e0",
                                  background: "#e4ffe4",
                                }}
                              >
                                {val(tray?.costPerTray)}
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderRight: "2px solid #e0e0e0",
                                  background: "#e4ffe4",
                                }}
                              >
                                {trayTotal}
                              </TableCell>

                              {/* TOTALS */}
                              {index === 0 && (
                                <>
                                  <TableCell
                                    rowSpan={rowCount}
                                    sx={{
                                      borderRight: "2px solid #e0e0e0",
                                      backgroundColor: tx.discount ? "#fff9e6" : "inherit",
                                      fontWeight: tx.discount ? "bold" : "normal",
                                      color: tx.discount ? "#b45309" : "inherit",
                                    }}
                                  >
                                    {tx.discount ? `- ₹${Number(tx.discount).toLocaleString("en-IN")}` : "-"}
                                  </TableCell>
                                  <TableCell
                                    rowSpan={rowCount}
                                    sx={{ borderRight: "2px solid #e0e0e0" }}
                                  >
                                    {val(paymentAmount) || "-"}
                                  </TableCell>
                                  <TableCell
                                    rowSpan={rowCount}
                                    sx={{
                                      borderRight: "2px solid #e0e0e0",
                                    }}
                                  >
                                    {val(traysTotal) ? val(traysTotal) : "-"}
                                  </TableCell>
                                  <TableCell
                                    rowSpan={rowCount}
                                    sx={{ borderRight: "2px solid #e0e0e0" }}
                                  >
                                    {val(grandTotal) ? val(grandTotal) : "-"}
                                  </TableCell>
                                  <TableCell
                                    rowSpan={rowCount}
                                    sx={{ borderRight: "2px solid #e0e0e0" }}
                                  >
                                    {val(tx.transactionBy?.fullName)} <br />{" "}
                                    {new Date(tx?.createdAt).toLocaleDateString(
                                      "en-IN",
                                      {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                        timeZone: "Asia/Kolkata",
                                      }
                                    )}
                                  </TableCell>
                                  <TableCell rowSpan={rowCount}>
                                    <Box display="flex" alignItems="center">
                                      {user?.roleId !== 3 && (
                                        <IconButton
                                          color="primary"
                                          onClick={() =>
                                            openEditTransactionPopup(tx)
                                          }
                                        >
                                          <FaEdit size={19} />
                                        </IconButton>
                                      )}
                                      <IconButton
                                        color="primary"
                                        onClick={() => printTransaction(tx)}
                                      >
                                        <FaPrint size={19} />
                                      </IconButton>
                                      <IconButton
                                        color="primary"
                                        onClick={() =>
                                          shareTransactionToWhatsapp(tx)
                                        }
                                        title="Share on WhatsApp"
                                      >
                                        <FaWhatsapp size={19} />
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          );
                        })}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={transactionList.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}

        {/* Transaction Dialog */}
        <Dialog open={open} maxWidth="md" fullWidth>
          <DialogTitle
            variant="h6"
            fontWeight="bold"
            sx={{
              background: "#f5f5f5",
            }}
          >
            {editTransaction ? "Edit Transaction" : "Add Transaction"}
          </DialogTitle>

          <form onSubmit={handleSubmit(addTransaction)}>
            <DialogContent>
              <Box display="flex" gap={4} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Controller
                      name="stockGiven"
                      control={control}
                      render={({ field }) => (
                        <Checkbox {...field} checked={field.value} />
                      )}
                    />
                  }
                  label="Sales"
                />
                <FormControlLabel
                  control={
                    <Controller
                      name="paymentDone"
                      control={control}
                      render={({ field }) => (
                        <Checkbox {...field} checked={field.value} />
                      )}
                    />
                  }
                  label="Payment Received"
                />
                <FormControlLabel
                  control={
                    <Controller
                      name="traysReturned"
                      control={control}
                      render={({ field }) => (
                        <Checkbox {...field} checked={field.value} />
                      )}
                    />
                  }
                  label="Tray Payment"
                />
                <FormControlLabel
                  control={
                    <Controller
                      name="discountGiven"
                      control={control}
                      render={({ field }) => (
                        <Checkbox {...field} checked={field.value} />
                      )}
                    />
                  }
                  label="Add Discount"
                />
              </Box>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Controller
                  name="dateOfTransaction"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      sx={{ mt: 3 }}
                      label="Date of Transaction"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(v) => field.onChange(v ? v.toDate() : null)}
                      maxDate={dayjs()}
                      slotProps={{
                        textField: {
                          fullWidth: false,
                          error: !!fieldState.error,
                          helperText: fieldState.error?.message,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>

              {stockGiven && (
                <>
                  <Divider sx={{ my: 2 }} />

                  <Typography variant="p" fontWeight="bold">
                    Sales
                  </Typography>

                  {salesFields.map((row, index) => {
                    const qty = watch(`sales.${index}.numberOfItems`);
                    const cost = watch(`sales.${index}.costPerItem`);

                    return (
                      <Box
                        key={row.id}
                        display="flex"
                        gap={2}
                        mt={2}
                        alignItems={"center"}
                      >
                        {/* <TextField
                          label="Item Name"
                          sx={{ width: "45%", flex: 1 }}
                          {...register(`sales.${index}.itemName`)}
                          error={!!errors?.sales?.[index]?.itemName}
                          helperText={errors?.sales?.[index]?.itemName?.message}
                        /> */}
                        <FormControl
                          sx={{ flex: 1 }}
                          error={!!errors?.sales?.[index]?.itemName}
                        >
                          <InputLabel>Item</InputLabel>

                          <Controller
                            name={`sales.${index}.itemName`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Item"
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange(e.target.value)}
                              >
                                {items.map((item) => (
                                  <MenuItem
                                    key={item._id}
                                    value={item.itemName}
                                  >
                                    {item.itemName}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />

                          <FormHelperText>
                            {errors?.sales?.[index]?.itemName?.message}
                          </FormHelperText>
                        </FormControl>
                        <FormControl
                          sx={{ flex: 1 }}
                          error={!!errors?.sales?.[index]?.trayTypeId}
                        >
                          <InputLabel>Tray Type</InputLabel>

                          <Controller
                            name={`sales.${index}.trayTypeId`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Tray Type"
                                value={field.value ?? ""}
                                onChange={(e) =>
                                  field.onChange(Number(e.target.value))
                                }
                              >
                                {activeTrayTypes.map((tray) => (
                                  <MenuItem
                                    key={tray.trayTypeId || tray.id}
                                    value={tray.trayTypeId || tray.id}
                                  >
                                    {tray.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />

                          <FormHelperText>
                            {errors?.sales?.[index]?.trayTypeId?.message}
                          </FormHelperText>
                        </FormControl>

                        <TextField
                          sx={{ flex: 1 }}
                          label="Qty"
                          type="number"
                          {...register(`sales.${index}.numberOfItems`, {
                            setValueAs: (v) =>
                              v === "" ? undefined : Number(v),
                          })}
                          error={!!errors?.sales?.[index]?.numberOfItems}
                          helperText={
                            errors?.sales?.[index]?.numberOfItems?.message
                          }
                        />
                        <TextField
                          label="Cost"
                          type="number"
                          onWheel={(e) => e.target.blur()}
                          sx={{ flex: 1 }}
                          {...register(`sales.${index}.costPerItem`, {
                            setValueAs: (v) =>
                              v === "" ? undefined : Number(v),
                          })}
                          error={!!errors?.sales?.[index]?.costPerItem}
                          helperText={
                            errors?.sales?.[index]?.costPerItem?.message
                          }
                        />
                        <TextField
                          label="Amount"
                          readonly
                          sx={{ flex: 1 }}
                          value={qty && cost ? qty * cost : ""}
                        />

                        {salesFields.length > 1 && (
                          <Button color="error" onClick={() => remove(index)}>
                            <FaTrash />
                          </Button>
                        )}
                      </Box>
                    );
                  })}

                  <Button
                    sx={{ mt: 2 }}
                    variant="outlined"
                    onClick={() =>
                      append({
                        itemName: "",
                        numberOfItems: "",
                        costPerItem: "",
                      })
                    }
                  >
                    + Add More
                  </Button>

                  <Box display="flex" alignItems="center" mt={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...register("labourCharges.enabled")}
                          checked={Boolean(labourEnabled)}
                        />
                      }
                      label="Add Loading Charges"
                    />
                    {editTransaction && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...register("damageRefund.enabled")}
                            checked={Boolean(damageRefundEnabled)}
                          />
                        }
                        label="Add Damage Refund"
                      />
                    )}
                  </Box>

                  {labourEnabled && (
                    <>
                      <Typography variant="p" fontWeight="bold">
                        Loading Charges
                      </Typography>
                      <Box display="flex" gap={2} mt={2} alignItems="center">
                        <TextField
                          label="Qty"
                          type="number"
                          sx={{ flex: 1 }}
                          value={totalSalesQty || ""}
                          InputProps={{ readOnly: true }}
                        />
                        <TextField
                          label="Cost"
                          type="number"
                          onWheel={(e) => e.target.blur()}
                          sx={{ flex: 1 }}
                          {...register(`labourCharges.costPerItem`, {
                            setValueAs: (v) =>
                              v === "" ? undefined : Number(v),
                          })}
                          error={!!errors?.labourCharges?.costPerItem}
                          helperText={
                            errors?.labourCharges?.costPerItem?.message
                          }
                        />
                        <TextField
                          label="Total Amount"
                          sx={{ flex: 1 }}
                          value={
                            totalSalesQty && labourCost
                              ? totalSalesQty * Number(labourCost)
                              : ""
                          }
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                    </>
                  )}



                  {editTransaction && damageRefundEnabled && (
                    <>
                      <Typography variant="p" fontWeight="bold">
                        Damage Refund
                      </Typography>
                      {damageRefundForm.fields.map((field, index) => {
                        const currentRefund = damageRefunds[index] || {};
                        const selectedSaleIndex = Number.isFinite(
                          Number(currentRefund.selectedSaleIndex)
                        )
                          ? Number(currentRefund.selectedSaleIndex)
                          : undefined;
                        const selectedSale = Number.isFinite(selectedSaleIndex)
                          ? salesValues[selectedSaleIndex]
                          : null;
                        const selectedTrayType =
                          selectedSale?.trayTypeId && trayTypes.length
                            ? trayTypes.find(
                                (tray) =>
                                  tray.trayTypeId === selectedSale.trayTypeId ||
                                  tray.id === selectedSale.trayTypeId
                              )
                            : null;
                        const costPerItem = selectedSale
                          ? Math.max(
                              Number(selectedSale.costPerItem || 0) -
                                Number(selectedTrayType?.price || 0),
                              0
                            )
                          : "";
                        const maxQty = Number(selectedSale?.numberOfItems || 0);
                        const qty = Number(currentRefund.numberOfItems || 0);
                        const totalRefund =
                          costPerItem && qty ? qty * costPerItem : "";

                        return (
                          <Box
                            display="flex"
                            gap={2}
                            mt={2}
                            alignItems="center"
                            key={field.id}
                          >
                            <FormControl
                              sx={{ flex: 1 }}
                              error={
                                !!errors?.damageRefunds?.[index]
                                  ?.selectedSaleIndex
                              }
                            >
                              <InputLabel>Select Item</InputLabel>
                              <Controller
                                name={`damageRefunds.${index}.selectedSaleIndex`}
                                control={control}
                                render={({ field: selectField }) => (
                                  <Select
                                    {...selectField}
                                    value={selectField.value ?? ""}
                                    label="Select Item"
                                    onChange={(e) => {
                                      selectField.onChange(
                                        Number(e.target.value)
                                      );
                                      setValue(
                                        `damageRefunds.${index}.numberOfItems`,
                                        undefined,
                                        {
                                          shouldValidate: true,
                                          shouldDirty: true,
                                        }
                                      );
                                    }}
                                  >
                                    {salesValues.map((sale, idx) => (
                                      <MenuItem
                                        key={`${sale.itemName}-${idx}`}
                                        value={idx}
                                      >
                                        {sale.itemName || `Item ${idx + 1}`}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                )}
                              />
                              <FormHelperText>
                                {
                                  errors?.damageRefunds?.[index]
                                    ?.selectedSaleIndex?.message
                                }
                              </FormHelperText>
                            </FormControl>

                            <TextField
                              sx={{ flex: 1 }}
                              label="Qty"
                              type="number"
                              inputProps={{
                                min: 1,
                                max: maxQty,
                              }}
                              {...register(
                                `damageRefunds.${index}.numberOfItems`,
                                {
                                  setValueAs: (v) =>
                                    v === "" ? undefined : Number(v),
                                  validate: (value) => {
                                    if (!value) return true;
                                    return (
                                      value <= maxQty ||
                                      `Maximum allowed is ${maxQty}`
                                    );
                                  },
                                }
                              )}
                              error={
                                !!errors?.damageRefunds?.[index]?.numberOfItems
                              }
                              helperText={
                                errors?.damageRefunds?.[index]?.numberOfItems
                                  ?.message
                              }
                            />

                            <TextField
                              label="Cost"
                              type="number"
                              sx={{ flex: 1 }}
                              value={costPerItem}
                              InputProps={{ readOnly: true }}
                            />

                            <TextField
                              label="Total Refund"
                              sx={{ flex: 1 }}
                              value={totalRefund}
                              InputProps={{ readOnly: true }}
                            />

                            {damageRefundForm.fields.length > 1 && (
                              <Button
                                color="error"
                                onClick={() => damageRefundForm.remove(index)}
                              >
                                <FaTrash />
                              </Button>
                            )}
                          </Box>
                        );
                      })}

                      <Button
                        sx={{ mt: 2 }}
                        variant="outlined"
                        onClick={() =>
                          damageRefundForm.append({
                            selectedSaleIndex: undefined,
                            numberOfItems: undefined,
                            costPerItem: undefined,
                            total: undefined,
                          })
                        }
                      >
                        + Add More
                      </Button>
                    </>
                  )}
                </>
              )}

              {paymentDone && (
                <>
                  <Divider sx={{ my: 2 }} />

                  <Typography variant="p" fontWeight="bold">
                    Payment
                  </Typography>

                  <Box display="flex" gap={2} mt={2}>
                    <TextField
                      fullWidth
                      label="Amount"
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      {...register(`paymentAmount`, {
                        setValueAs: (v) => (v === "" ? undefined : Number(v)),
                      })}
                      error={!!errors?.paymentAmount}
                      helperText={errors?.paymentAmount?.message}
                    />
                  </Box>
                </>
              )}

              {discountGiven && (
                <>
                  <Divider sx={{ my: 2 }} />

                  <Typography variant="p" fontWeight="bold">
                    Discount
                  </Typography>

                  <Box display="flex" gap={2} mt={2}>
                    <TextField
                      fullWidth
                      label="Amount"
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      {...register(`discount`, {
                        setValueAs: (v) =>
                          v === "" ? undefined : Number(v),
                      })}
                      error={!!errors?.discount}
                      helperText={errors?.discount?.message}
                    />
                  </Box>
                </>
              )}

              {traysReturned && (
                <>
                  <Divider sx={{ my: 2 }} />

                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography fontWeight="bold">Tray Payment</Typography>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={excludePayment}
                          onChange={(e) => setExcludePayment(e.target.checked)}
                        />
                      }
                      label="Exclude Payment"
                    />
                  </Box>

                  {trayForm.fields.map((row, index) => {
                    const qty = watch(`trays.${index}.numberOfTrays`) ?? 0;
                    const trayId = watch(`trays.${index}.trayTypeId`);
                    const isExcluded = excludePayment;
                    const selectedTrayType =
                      trayId && trayTypes.length
                        ? trayTypes.find(
                            (tray) =>
                              tray.trayTypeId === trayId || tray.id === trayId
                          )
                        : null;
                    const costOfSelectedTray = selectedTrayType?.price || 0;
                    const availableBalance = getTrayBalanceCount(trayId);

                    const originalQty =
                      editTransaction &&
                      selectedTransaction?.trays?.[index]?.numberOfTrays
                        ? Number(selectedTransaction.trays[index].numberOfTrays)
                        : 0;

                    const maxQty = editTransaction
                      ? availableBalance + originalQty
                      : availableBalance;

                    const availableTrayTypes = (
                      customerDetails?.trayBalance || []
                    ).filter((tray) => {
                      const currentTrayId = tray.trayTypeId || tray.id;

                      if (currentTrayId === trayId) return true;
                      const hasValidCount =
                        tray.count !== undefined &&
                        tray.count !== null &&
                        Number(tray.count) > 0;

                      const isNotSelected =
                        !selectedTrayTypeIds.includes(currentTrayId);

                      return hasValidCount && isNotSelected;
                    });

                    return (
                      <div>
                        <Box
                          key={row.id}
                          display="flex"
                          gap={2}
                          mt={2}
                          alignItems="center"
                        >
                          <FormControl
                            sx={{ flex: 1 }}
                            error={!!errors?.trays?.[index]?.trayTypeId}
                          >
                            <InputLabel>Tray Type</InputLabel>

                            <Controller
                              name={`trays.${index}.trayTypeId`}
                              control={control}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  label="Tray Type"
                                  value={field.value ?? ""}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                >
                                  {availableTrayTypes.map((tray) => (
                                    <MenuItem key={tray.id} value={tray.id}>
                                      {tray.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />

                            <FormHelperText>
                              {errors?.trays?.[index]?.trayTypeId?.message}
                            </FormHelperText>
                          </FormControl>

                          <TextField
                            sx={{ flex: 1 }}
                            label="Qty"
                            type="number"
                            inputProps={{
                              min: 1,
                              max: maxQty,
                            }}
                            {...register(`trays.${index}.numberOfTrays`, {
                              setValueAs: (v) =>
                                v === "" ? undefined : Number(v),
                              validate: (value) =>
                                value <= maxQty ||
                                `Maximum allowed is ${maxQty}`,
                            })}
                            error={!!errors?.trays?.[index]?.numberOfTrays}
                            helperText={
                              errors?.trays?.[index]?.numberOfTrays?.message
                            }
                          />
                          {/* <TextField
                          label="Cost"
                          type="number"
                          {...register(`trays.${index}.costPerTray`, {
                            setValueAs: (v) =>
                              v === "" ? undefined : Number(v),
                          })}
                          error={!!errors?.trays?.[index]?.costPerTray}
                          helperText={
                            errors?.trays?.[index]?.costPerTray?.message
                          }
                        /> */}

                          {!isExcluded && (
                            <TextField
                              label="Amount"
                              slotProps={{ input: { readOnly: true } }}
                              value={qty * costOfSelectedTray}
                              sx={{ flex: 1 }}
                            />
                          )}

                          {trayForm.fields.length > 1 && (
                            <Button
                              color="error"
                              onClick={() => trayForm.remove(index)}
                            >
                              <FaTrash />
                            </Button>
                          )}
                        </Box>
                      </div>
                    );
                  })}

                  {selectedTrayTypeIds.length < filteredTrayTypes.length && (
                    <Button
                      sx={{ mt: 2 }}
                      variant="outlined"
                      onClick={() =>
                        trayForm.append({
                          numberOfTrays: "",
                          costPerTray: "",
                        })
                      }
                    >
                      + Add More
                    </Button>
                  )}
                </>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => {
                  closeTransactionPopup();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={saving || (!stockGiven && !paymentDone && !discountGiven && !traysReturned)}>
                {saving ? "Saving" : "Submit"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* ✅ Payment Dialog */}
        <Dialog
          open={openPayment}
          fullWidth
          maxWidth="sm"
          onClose={closePaymentPopup}
        >
          <DialogTitle>Add Payment</DialogTitle>

          <form noValidate onSubmit={paymentForm.handleSubmit(updatePayment)}>
            <DialogContent>
              <Box display="grid" gap={2} mt={1}>
                <Box display="flex" gap={2}>
                  <TextField
                    label="Amount"
                    type="number"
                    fullWidth
                    required
                    {...paymentForm.register("amount")}
                    error={!!paymentForm.formState.errors.amount}
                    helperText={paymentForm.formState.errors.amount?.message}
                    InputProps={{
                      startAdornment: <span style={{ marginRight: 6 }}>₹</span>,
                    }}
                  />

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Controller
                      name="paymentDate"
                      control={paymentForm.control}
                      render={({ field, fieldState }) => (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Payment Date"
                            value={field.value ? dayjs(field.value) : null}
                            onChange={(newValue) =>
                              field.onChange(
                                newValue ? newValue.toDate() : null
                              )
                            }
                            maxDate={dayjs()}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                required: true,
                                error: !!fieldState?.error,
                                helperText: fieldState?.error?.message,
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </LocalizationProvider>
                </Box>

                <Box display="flex" gap={2}>
                  <TextField
                    select
                    label="Payment Method"
                    fullWidth
                    required
                    defaultValue=""
                    {...paymentForm.register("paymentMethod")}
                    error={!!paymentForm.formState.errors.paymentMethod}
                    helperText={
                      paymentForm.formState.errors.paymentMethod?.message
                    }
                  >
                    <MenuItem value="NEFT">NEFT</MenuItem>
                    <MenuItem value="UPI">UPI</MenuItem>
                    <MenuItem value="Net Banking">Net Banking</MenuItem>
                    <MenuItem value="Cash">Cash</MenuItem>
                  </TextField>

                  <TextField
                    label="Reference ID"
                    fullWidth
                    {...paymentForm.register("referenceId")}
                    error={!!paymentForm.formState.errors.referenceId}
                    helperText={
                      paymentForm.formState.errors.referenceId?.message
                    }
                    placeholder="Enter transaction or reference number"
                  />
                </Box>
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={closePaymentPopup}>Cancel</Button>
              <Button type="submit" variant="contained">
                Submit
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        <TrayBalanceModal
          open={openTrayModal}
          onClose={() => setOpenTrayModal(false)}
          trayBalance={customerDetails?.trayBalance}
        />
        <TransactionReceipt
          open={showReceipt}
          onClose={() => {
            setShowReceipt(false);
            setReceiptTransaction(null);
          }}
          transaction={receiptTransaction}
          customerDetails={customerDetails}
          accountBalanceData={{
            totalTransactionAmount: grandTotalTransactionAmount,
            totalPaidAmount: grandTotalPaidAmount,
            trayPaymentAmount: grandTrayPaymentAmount,
            totalAmountDue:
              grandTotalTransactionAmount +
              labourCharges -
              (grandTotalPaidAmount +
                grandTrayPaymentAmount +
                discountAmount +
                damageRefund),
          }}
          trayTypes={trayTypes}
          employerName={user?.employerName}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={openDeleteDialog}
        onClose={cancelDeleteCustomer}
        onConfirm={confirmDeleteCustomer}
        title="Delete Customer"
        message="Are you sure you want to delete this customer? This action cannot be undone and will remove all associated data."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
}

export default CustomerDetails;
